import {UploadedFile} from '_metronic/helpers/components/uploadTypes'
import {ID, Response} from '../../../../../../_metronic/helpers'
import {SearchSelectOption} from '../../../../../../_metronic/helpers/components/SearchSelect'
import {Product} from '../../../product-management/products-list/core/_models'
import {StuffidFileTag} from '../../../stuffidFileTag-management/stuffidFileTags-list/core/_models'
import {Customer} from '../../../customer-management/customers-list/core/_models'
import type {InvoiceProformaInfo} from './invoiceProforma'
/** The CRM sales case that registered an invoice version (entry source 15); only on one invoice's details. */
export type InvoiceCrmLinkInfo = {
  caseId: number
  caseNo?: string | null
  expert?: string | null
  registeredAt?: string | null
}
export type Invoice = {
  id?: ID
  hsh?: string
  cdt?: string
  cdtg?: string
  sln?: string
  slt?: 0 | 1
  slc?: string
  slec?: string
  sla?: string
  slid?: number
  slp?: string
  slz?: string
  bun?: string
  but?: 1 | 2 | 3 | 4
  buc?: string
  buci?: number
  bud?: string
  buec?: string
  bua?: string
  bup?: string
  buz?: string
  bbc?: string
  cnc?: string
  dcno?: string
  ofc?: string
  sanno?: string
  sandt?: string
  sandtg?: string
  bno?: string
  ctno?: string
  ctd?: string
  ctdg?: string
  ipno?: string
  eipno?: string
  wbno?: string
  shpin?: InvoiceShipmentInfo
  fltin?: InvoiceFlightInfo
  act?: boolean
  ipgs?: SearchSelectOption[]
  hasSubRow?: boolean
  canUndoEdit?: boolean
  canDeleteFailedCancellation?: boolean
  canDelete?: boolean
  canDeleteReasonCode?: string
  subRows: Invoice[]
  ph?: {
    n: string
    i: number
    ic?: string
    cl?: string
  }
  // Set only for a proforma (ph.i === 0): validity, terms and the invoice it became.
  prf?: InvoiceProformaInfo
  crm?: InvoiceCrmLinkInfo
  snd?: {
    isc: boolean
    sst?: number
    dt: string
    rf: string
    st: string
    // Sent, but the tax system's answer was lost; settled by inquiry. Also set on phase-1 rows.
    amb?: boolean
    // TaxId of that ambiguous send, which may differ from txi after a later date change.
    stx?: string
  }
  wei?: string
  prc?: string
  dis?: string
  dist?: string
  adp?: string
  adpc?: string
  crc?: string
  vtp?: string
  ntp?: string
  ntps?: string
  txi?: string
  srl?: number
  fmi?: number
  fid?: string
  fmt?: string
  fme?: 'Production' | 'Sandbox'
  fmet?: string
  fmd?: boolean
  fmce?: boolean
  fmcr?: string
  no?: string
  idt?: string
  idtg?: string
  ridt?: string
  idtp?: {
    y?: number
    m?: number
    mn?: string
    d?: number
    cl?: string
  }
  stl?: string
  stli?: number
  ctmi?: number
  tmpi?: number
  crd?: string
  crdi?: string
  crdv?: string
  csh?: string
  cshi?: string
  cshv?: string
  tmp?: string
  lbl?: number
  sbj?: string
  sbji?: number
  edt?: boolean
  typ?: string
  typi?: number
  itm?: InvoiceItem[]
  pym?: InvoicePayment[]
  pymt?: number
  pymr?: number
  tdis?: InvoiceDiscount[]
  fls?: UploadedFile[]
  lg?: string
  sig?: string
  dsc?: string
  tdsc?: string
  qr?: string
  tlg?: boolean
  tgs?: number
  stxi?: string
  tx17?: number
  bst?: number
  bsr?: string
  a6s?: string
  canManualBuyerStatusUpdate?: boolean
  brw?: BuyerReactionWindow
  tvr?: number
  pp?: boolean
  pytx?: InvoicePaymentRegistrationInfo
  ent?: number
  instructionVersion?: string
  insr?: number
  indati2m?: number
  nti1?: string
  nti2?: string
  // Pattern 11 (commodity exchange) only: the chain's exchange sale announcement confirmation.
  // The server omits both fields for every other invoice, which keeps the controls hidden.
  exchangeAnnouncement?: InvoiceExchangeAnnouncementSummary
  canSetExchangeAnnouncementStatus?: boolean
}

export type ExchangeAnnouncementStatus = 'notRecorded' | 'notConfirmed' | 'confirmed'
export type ExchangeAnnouncementSource = 'manual' | 'orgApi'
export type ExchangeAnnouncementChannel = 'panel' | 'apiV2' | 'orgApi' | 'script'

export type InvoiceExchangeAnnouncementSummary = {
  status: ExchangeAnnouncementStatus
  source?: ExchangeAnnouncementSource
  // Display name of the user who recorded a manual status.
  setBy?: string
  // Shamsi text prepared by the server; setAtUtc is the ISO fallback.
  setAt?: string
  setAtUtc?: string
}

export type BuyerReactionWindow = {
  sdt: string
  ddt: string
  rd: number
  od: boolean
  ai: boolean
}

export type InvoiceSubRow = {
  id: ID
  isOpen: boolean
  rows: Invoice[]
  /** The last reading after a list answer failed, so the rows shown may be older than the list. */
  refreshFailed?: boolean
}

export type stuffidFileTagSubRow = {
  id: ID
  isOpen: boolean
  rows: StuffidFileTag[]
}

export type CustomerSubShow = {
  id: ID
  isOpen: boolean
  rows: Customer[]
}

export type EbtalSubShow = {
  id: ID
  isOpen: boolean
  rows: Customer[]
}

export type ProductSubShow = {
  id: ID
  isOpen: boolean
  rows: Product[]
}

export type InvoiceItem = {
  id: number
  sop: {
    id: number
    cd: string
    sti: string
    nm: string
    lnk?: string
  }
  cd: string
  sti: string
  nm: string
  dsc: string
  wei: string
  qty: string
  unt: string
  uni: number
  /** The unit's official code; null for a unit without one (sent without mu). Absent from older servers. */
  unc?: string | null
  amt: string
  prc: string
  wge: string
  prft: string
  cara: string
  comm: string
  swpc: string
  dis: string
  tdis: string
  adp: string
  cnc: string
  adpd: string
  /** The currency code (USD), not its id. */
  cur: string
  /** The currency's id, what the currency list and currencyId use. Absent from servers before backend 1.0.0.787. */
  curi?: number | null
  curt: string
  curv: string
  adpr: string
  adppr: string
  vtbr?: number
  vtr: number
  vtp: string
  gvr: string
  gvp: string
  ntp: string
  gtn: string
  gtr: number
  gtp: string
  txn: string
  txr: number
  txp: string
}

export type InvoicePayment = {
  cno?: string
  dsc?: string
  dt?: string
  id?: number
  nid?: string
  prc?: number
  csh?: number
  vt?: number
  trc?: string
  typ?: number
  fl?: UploadedFile[]
  swno?: string
  aqno?: string
  terno?: string
  switchNumber?: string
  acquirerNumber?: string
  terminalNumber?: string
  txok?: boolean
  txerr?: string
  taxSendable?: boolean
  taxValidationError?: string
  // ثبت پرداخت در سامانه مؤدیان (invoice-payment): آخرین وضعیت ردیف و قفل بودن آن
  txrs?: number
  txrc?: string
  txrm?: string
  txrd?: string
  txrid?: number
  // مبلغ ثبت‌شده در کارپوشه (paidAmount): مبلغ ردیف بدون سهم مالیات بر ارزش افزوده
  txra?: number
  lck?: boolean
}

// وضعیت ثبت پرداخت‌های صورتحساب ارسال‌شده در سامانه مؤدیان (InvoicePaymentTaxRegistrationStates)
export const PaymentTaxRegistrationState = {
  Queued: 1,
  Registered: 2,
  Rejected: 3,
  Unknown: 4,
  NotApplicable: 5,
  Exhausted: 6,
} as const

export type InvoicePaymentRegistrationInfo = {
  el: boolean
  lk: boolean
  cd?: string
  rs?: string
}

export type InvoicePaymentTaxRegistration = {
  status?: number
  code?: string
  message?: string
  date?: string
  registrationId?: number
  // مبلغی که به کارپوشه فرستاده شده (بدون سهم مالیات بر ارزش افزوده)
  amount?: number
}

export type InvoiceDiscount = {
  id?: number
  prc?: number
  rt?: number
  dsc?: string
}

export type InvoiceForm = {
  id?: number
  no?: string
  date?: string
  seller?: number
  buyer?: number
  buyerType?: number
  buyerNid?: string
  buyerPhone?: string
  buyerAddress?: string
  buyerZipCode?: string
  rows?: InvoiceRowForm[]
  sumPrice?: number
  sumDiscount?: number
  sumAdPrice?: number
  sumVat?: number
  sumTax?: number
  sumNetPrice?: number
  settlement?: number
  cash?: number
}

export type InvoiceRowForm = {
  id?: number
  productId?: number
  product: Product
  amount?: number
  qty?: number
  discount?: number
  contractNo?: number
  wage?: number
  carat?: number
  profit?: number
  commission?: number
  totalDiscount?: number
  description?: string
  price?: number
  adPrice?: number
  // الگوی ۱۱: مبلغ بعد از تخفیف (ارزش معامله در اعلامیه فروش بورس)، ریال صحیح
  adis?: number
  vatRate?: number
  vatRateManuallyChanged?: boolean
  vatPrice?: number
  taxName?: string
  taxRate?: number
  taxPrice?: number
  govTaxName?: string
  govTaxRate?: number
  govTaxPrice?: number
  netPrice?: number
  unit?: SearchSelectOption
  currencyRate?: number
  currencyValue?: number
  amountCurrency?: number
  currencyId?: number
  currencyPurchaseRate?: number
  vatBaseAmount?: number
  weight?: number
  currency?: SearchSelectOption
}

export type InvoiceFlightInfo = {
  buyerMethod?: number
  flightType?: number
  economicCode?: string
  firstName?: string
  foreignId?: string
  lastName?: string
  nationalId?: string
  passportNumber?: string
  agentEconomicCode?: string
  agentName?: string
}

export type InvoiceShipmentInfo = {
  blno?: string
  rblno?: string
  orco?: string
  orci?: string
  dsco?: string
  dsci?: string
  sneco?: string
  rceco?: string
  trtyp?: string
  flno?: string
  drcd?: string
  ttw?: string
}

export type InvoiceBuyer = {
  id: number
  nm: string
  nid: string
}

export type InvoiceDiscountForm = {
  id: number
  description: string
  price: number
  rate: string
  type: 'rate' | 'price'
}
export type InvoicePaymentForm = {
  id: number
  description?: string
  price: number
  cash: number
  vat: number
  date: string
  type: string
  refereceNo?: string
  traceNo?: string
  cardNo?: string
  payerId?: string
  payerNationalCode?: string
  switchNumber?: string
  acquirerNumber?: string
  terminalNumber?: string
  onlinePosTransactionId?: number
  paymentDateTime?: string
  amount?: number
  trackingNumber?: string
  payerCardNumber?: string
  paymentMethod?: string
  files?: UploadedFile[]
  taxValidationError?: string
  // ردیف ثبت‌شده (یا در دست ثبت) در سامانه مؤدیان: ویرایش و حذف ندارد
  locked?: boolean
  taxRegistration?: InvoicePaymentTaxRegistration
}

export type InvoicesQueryResponse = Response<Array<Invoice>>

export type InvoiceListSeasonOption = {
  key: string
  year: number
  season: number
  name: string
  fromDate: string
  toDate: string
  color: string
  count: number
  amount: number
  amountExact?: string
  selected?: boolean
}

// The keys of the list's «وضعیت ارسال» filter; a status card filters the list by its key.
export type InvoiceListSendStatusKey =
  | 'notsend'
  | 'errored'
  | 'pending'
  | 'unknown'
  | 'success'
  | 'canceled'

export type InvoiceListSummaryStatus = {
  key: InvoiceListSendStatusKey
  label: string
  color: string
  icon: string
  count: number
  amount: number
  amountExact?: string
  percent: number
}

// Counted live by api/invoices/summary/statuses for the list's own season.
export type InvoiceListSendStatuses = {
  season: string
  totalCount: number
  totalAmount: number
  totalAmountExact?: string
  statuses: InvoiceListSummaryStatus[]
  generatedAt?: string
}

export type InvoiceListSummaryBreakdown = {
  key: string
  label: string
  color: string
  icon: string
  count: number
  amount: number
  amountExact?: string
  percent: number
}

export type InvoiceListTrendPoint = {
  date: string
  label: string
  shortLabel: string
  count: number
  amount: number
  amountExact?: string
}

export type InvoiceListSummary = {
  selectedSeason?: InvoiceListSeasonOption
  seasons: InvoiceListSeasonOption[]
  totalCount: number
  totalAmount: number
  totalAmountExact?: string
  vatAmount: number
  vatAmountExact?: string
  cashAmount: number
  cashAmountExact?: string
  creditAmount: number
  creditAmountExact?: string
  paymentTotal?: number
  paymentTotalExact?: string
  payments: InvoiceListSummaryBreakdown[]
  templates: InvoiceListSummaryBreakdown[]
  types: InvoiceListSummaryBreakdown[]
  trendMode?: 'daily' | 'monthly' | 'seasonal'
  trend: InvoiceListTrendPoint[]
  generatedAt?: string
}

export interface IInvoiceTypeTemplate {
  id: number
  name: string
  shortName: string
  description: string
  icon: string
  color: string
  isActive: boolean
  /**
   * Listed on the organisation's technical docs site (tp.tax.gov.ir/docs, 2026-10-21) but not in
   * RC_IITP.IS V7.9. Shown in lists and filters with this note; never offered for issuing
   * (isActive stays false).
   */
  notAnnounced?: boolean
  sum?: number
  vat?: number
  percent?: number
  count: number
  template?: IInvoiceTypeTemplate[]
}

export const NOT_ANNOUNCED_LABEL = 'دستورالعمل رسمی اعلام نشده'

const notAnnouncedDescription =
  'در سایت مستندات فنی سامانه مودیان آمده، ولی دستورالعمل رسمی آن اعلام نشده است و فعلاً قابل صدور نیست.'

export const InvoiceTypeList: IInvoiceTypeTemplate[] = [
  {
    id: 1,
    name: 'صورتحساب الکترونیکی نوع اول',
    description:
      'اطلاعات هویتی کامل خریدار ثبت شده و در صورت تأیید، اعتبار مالیاتی نیز برای ایشان محسوب می‌شود',
    icon: 'la-user-check',
    shortName: 'نوع اول',
    color: 'success',
    isActive: true,
    count: 0,
    template: [
      {
        id: 1,
        name: 'الگوی اول (فروش)',
        shortName: 'الگو فروش',
        description: 'صدور صورتحساب کامل فروش کالا یا خدمات',
        icon: 'la-file-invoice',
        color: 'primary',
        isActive: true,
        count: 0,
      },
      {
        id: 2,
        name: 'الگوی دوم (فروش ارز)',
        shortName: 'فروش ارز',
        description: 'صدور صورتحساب مبادلات ارزی واحدهای صرافی و دارای مجوز',
        icon: 'la-money-bill-wave',
        color: 'success',
        isActive: true,
        count: 0,
      },
      {
        id: 3,
        name: 'الگوی سوم (طلا، جواهر و پلاتین)',
        shortName: 'طلا و جواهر',
        description:
          'صدور صورتحساب فروش طلا، جواهر و پلاتین (ثبت اجرت ساخت، سود، و حق‌العمل الزامی است)',
        icon: 'la-ring',
        color: 'warning',
        isActive: true,
        count: 0,
      },
      {
        id: 4,
        name: 'الگوی چهارم (قرارداد پیمانکاری)',
        shortName: 'پیمانکاری',
        description:
          'صدور صورتحساب فروش در قراردادهای پیمانکاری (ثبت شناسه یکتای ثبت قرارداد الزامی است)',
        icon: 'la-hands-helping',
        color: 'info',
        isActive: true,
        count: 0,
      },
      {
        id: 5,
        name: 'الگوی پنجم (قبوض خدماتی)',
        shortName: 'قبوض',
        description: 'صدور قبوض خدماتی (ثبت شناسه قبض الزامی است)',
        icon: 'la-bolt',
        color: 'warning',
        isActive: true,
        count: 0,
      },
      {
        id: 6,
        name: 'الگوی ششم (بلیط هواپیما)',
        shortName: 'بلیط هواپیما',
        description: 'صدور بلیط پرواز داخلی و یا خارجی',
        icon: 'la-plane',
        color: 'primary',
        isActive: true,
        count: 0,
      },
      {
        id: 7,
        name: 'الگوی هفتم (صادرات)',
        shortName: 'صادرات',
        description: 'صدور صورتحساب فروش صادراتی (جهت بهره‌مندی از مشوق‌های مالیاتی)',
        icon: 'la-ship',
        color: 'info',
        isActive: true,
        count: 0,
      },
      {
        id: 8,
        name: 'الگوی هشتم (بارنامه)',
        shortName: 'بارنامه',
        description: 'صدور صورتحساب بارنامه',
        icon: 'la-truck-moving',
        color: 'warning',
        isActive: true,
        count: 0,
      },
      {
        id: 9,
        name: 'الگوی نهم (فروش فرآورده‌های نفتی پالایش و پخش)',
        shortName: 'فروش فرآورده‌های نفتی',
        description: 'صدور صورتحساب فروش فرآورده‌های نفتی پالایش و پخش',
        icon: 'la-oil-can',
        color: 'gray-600',
        isActive: true,
        count: 0,
      },
      {
        id: 11,
        name: 'الگوی یازدهم (بورس کالا)',
        shortName: 'بورس کالا',
        description: 'صدور صورتحساب بورس اوراق بهادار مبتنی بر کالا',
        icon: 'la-chart-line',
        color: 'success',
        isActive: true,
        count: 0,
      },
      {
        id: 13,
        name: 'الگوی سیزدهم (خدمات بیمه)',
        shortName: 'خدمات بیمه',
        description: 'صدور صورتحساب خدمات بیمه',
        icon: 'la-car-crash',
        color: 'info',
        isActive: true,
        count: 0,
      },
      {
        id: 14,
        name: 'الگوی چهاردهم (فروش زنجیره)',
        shortName: 'فروش زنجیره',
        description: 'صدور صورتحساب بورس فروش زنجیره',
        icon: 'la-link',
        color: 'primary',
        isActive: true,
        count: 0,
      },
      {
        id: 98,
        name: `الگوی ۹۸ (خرید از بورس کالا؛ ${NOT_ANNOUNCED_LABEL})`,
        shortName: 'خرید از بورس کالا',
        description: notAnnouncedDescription,
        icon: 'la-balance-scale',
        color: 'gray-600',
        isActive: false,
        notAnnounced: true,
        count: 0,
      },
      {
        id: 99,
        name: `الگوی ۹۹ (واردات؛ ${NOT_ANNOUNCED_LABEL})`,
        shortName: 'واردات',
        description: notAnnouncedDescription,
        icon: 'la-file-import',
        color: 'gray-600',
        isActive: false,
        notAnnounced: true,
        count: 0,
      },
    ],
  },
  {
    id: 2,
    name: 'صورتحساب الکترونیکی نوع دوم',
    description:
      'ثبت اطلاعات خریدار یا مصرف‌کننده نهایی اختیاری است و اعتبار مالیاتی نیز برای ایشان محسوب نخواهد شد',
    icon: 'la-user-slash',
    shortName: 'نوع دوم',
    color: 'primary',
    isActive: true,
    count: 0,
    template: [
      {
        id: 1,
        name: 'الگوی اول (فروش)',
        description: 'صدور صورتحساب کامل فروش کالا یا خدمات',
        icon: 'la-shopping-cart',
        shortName: 'فروش',
        color: 'primary',
        isActive: true,
        count: 0,
      },
      {
        id: 3,
        name: 'الگوی سوم (طلا، جواهر و پلاتین)',
        description:
          'صدور صورتحساب فروش طلا، جواهر و پلاتین (ثبت اجرت ساخت، سود، و حق‌العمل الزامی است) ',
        shortName: 'طلا و جواهر',
        icon: 'la-ring',
        color: 'warning',
        isActive: true,
        count: 0,
      },
      {
        id: 13,
        name: 'الگوی سیزدهم (خدمات بیمه)',
        shortName: 'خدمات بیمه',
        description: 'صدور صورتحساب خدمات بیمه',
        icon: 'la-car-crash',
        color: 'info',
        isActive: true,
        count: 0,
      },
      {
        id: 9,
        name: 'الگوی نهم (فروش فرآورده‌های نفتی پالایش و پخش)',
        shortName: 'فروش فرآورده‌های نفتی',
        description: 'صدور صورتحساب فروش فرآورده‌های نفتی پالایش و پخش',
        icon: 'la-oil-can',
        color: 'gray-600',
        isActive: true,
        count: 0,
      },
      {
        id: 98,
        name: `الگوی ۹۸ (خرید از بورس کالا؛ ${NOT_ANNOUNCED_LABEL})`,
        shortName: 'خرید از بورس کالا',
        description: notAnnouncedDescription,
        icon: 'la-balance-scale',
        color: 'gray-600',
        isActive: false,
        notAnnounced: true,
        count: 0,
      },
    ],
  },
  {
    id: 3,
    name: 'صورتحساب الکترونیکی نوع سوم',
    shortName: 'رسید',
    description:
      'رسید پرداخت وجه صادره از دستگاه کارتخوان بانکی یا درگاه الکترونیکی پرداخت، که به عنوان پایانه فروشگاهی کسب و کار (مؤدی) ثبت شده‌است',
    icon: 'la-credit-card',
    color: 'info',
    isActive: true,
    count: 0,
  },
  {
    id: 5,
    name: `صورتحساب الکترونیکی نوع ۵ (${NOT_ANNOUNCED_LABEL})`,
    shortName: 'نوع ۵',
    description: notAnnouncedDescription,
    icon: 'la-question-circle',
    color: 'gray-600',
    isActive: false,
    notAnnounced: true,
    count: 0,
  },
]

export const initialInvoice: Invoice = {
  id: 0,
  subRows: [],
}

export const InvoicePaymentTypeItems: SearchSelectOption[] = [
  {
    label: 'چک',
    value: '1',
  },
  {
    label: 'تهاتر',
    value: '2',
  },
  {
    label: 'وجه نقد',
    value: '3',
  },
  {
    label: 'کارت خوان pos',
    value: '4',
  },
  {
    label: 'درگاه پرداخت اینترنتی',
    value: '5',
  },
  {
    label: 'کارت به کارت',
    value: '6',
  },
  {
    label: 'انتقال به حساب',
    value: '7',
  },
  {
    label: 'سایر',
    value: '8',
  },
]
